import { useState } from "react";
import { CopyIcon, EditIcon } from "@chakra-ui/icons";
import { Menu, MenuList, MenuItem, MenuDivider } from "@chakra-ui/react";

import { useBrandQuery } from "utils/apollo/hooks/brand";
import { assertPresence } from "utils/assertions";
import { getFragmentData, gql } from "__generated__/gql";
import EditPanelContent from "components/Products/EditPanelContent";
import MoreMenuButton from "components/Shared/MoreMenuButton";
import DeleteSkuMenuItem, { DeleteSkuData } from "./DeleteSkuMenuItem";
import ArchiveSkuMenuItem, { OnArchiveSuccess } from "./ArchiveSkuMenuItem";
import Edit from "./Edit";
import View from "./View";
import { getSkuFormDefaultValues, SkuInputs } from "./utils";

const fragment = gql(/* GraphQL */ `
  fragment SelectedSkuDetailsFields on Sku {
    id
    name
    isArchived
    ...SkuPanelView
    ...SkuPanelEdit
    ...ArchiveSkuMenuItemSku
    ...DeleteSkuMenuItemSku
  }
`);

const query = gql(/* GraphQL */ `
  query SkuPanel($skuId: UUID!) {
    sku(id: $skuId) {
      id
      ...SelectedSkuDetailsFields
    }
  }
`);

type Props = {
  onArchiveSuccess?: OnArchiveSuccess;
  onClose: () => void;
  onDeleteSuccess: (data: DeleteSkuData) => void;
  onDuplicateSku: (skuData: SkuInputs) => void;
  skuId?: string;
};

export default function SkuPanel({
  onArchiveSuccess,
  onClose,
  onDeleteSuccess,
  onDuplicateSku,
  skuId,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);

  const { data, loading } = useBrandQuery(query, {
    skip: !skuId,
    variables: skuId ? { skuId } : undefined,
    returnPartialData: true,
  });

  const isLoading = loading || typeof window === "undefined";
  const sku = getFragmentData(fragment, data?.sku);

  return (
    <EditPanelContent
      onClose={onClose}
      description={isEditing ? "Edit SKU" : "SKU Details"}
      heading={sku?.name}
      isArchived={sku?.isArchived ?? undefined}
      isLoading={isLoading}
      headingControls={
        <Menu>
          <MoreMenuButton
            aria-label="SKU Actions"
            size="md"
            px=".2em"
            py=".2em"
            isDisabled={isEditing}
          />
          <MenuList fontSize="md">
            <MenuItem
              icon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              isDisabled={!sku?.id}
            >
              Edit SKU Details…
            </MenuItem>

            <MenuDivider />

            <MenuItem
              onClick={() => {
                assertPresence(sku);
                const currentValues = getSkuFormDefaultValues(sku);
                const duplicateData: SkuInputs = {
                  ...currentValues,
                  name: `${currentValues.name ?? ""} (copy)`,
                };
                onDuplicateSku(duplicateData);
              }}
              icon={<CopyIcon />}
              isDisabled={!sku?.id || isLoading}
            >
              Duplicate SKU
            </MenuItem>

            <MenuDivider />

            <ArchiveSkuMenuItem onArchiveSuccess={onArchiveSuccess} sku={sku} />

            <DeleteSkuMenuItem sku={sku} onCompleted={onDeleteSuccess} />
          </MenuList>
        </Menu>
      }
    >
      {isEditing ? (
        <Edit
          isLoading={isLoading}
          onCancel={() => setIsEditing(false)}
          onComplete={() => setIsEditing(false)}
          sku={sku}
        />
      ) : (
        <View sku={sku} isLoading={isLoading} />
      )}
    </EditPanelContent>
  );
}
